// ==============================================
// MyPolaris — Anchor Watch (remote view)
// ==============================================
//
// The same QR the Scout shows on its Remote view screen, here where it can
// be shared: copied, sent, or saved as an image. A Scout screen can only be
// scanned; this page is the second door to the same address.
//
// Two kinds of link, labelled: a FIXED address the skipper chose to keep for
// the Scout, or the address of the anchor watch running right now. Whether
// a link is LIVE is decided by asking the relay itself — a 404 means no
// watch is set — never from the Scout's last report alone. "Open remote
// view" is enabled only while the relay says the watch is live.
//
// This page can never reach the boat. It holds the read token only; the
// publish key never leaves the Scout.
// Package-gated via packages.anchor_remote_view (upsell card, sidebar hidden).
//
// Readability mandate: nothing under 14px (text-sm), primary copy 16px+.

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  QrCodeIcon, ArrowTopRightOnSquareIcon, ClipboardDocumentIcon, ArrowDownTrayIcon, ShareIcon,
  SignalIcon, SignalSlashIcon, LockClosedIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';
import { anchorRemoteUrl, anchorRelayJsonUrl } from '../../types/anchorRemote';
import type { AnchorRemoteDevice } from '../../types/anchorRemote';

const DEVICES_POLL_MS = 15_000;
const LIVE_POLL_MS = 10_000;
const ONLINE_WINDOW_MS = 3 * 60_000;

const isOnline = (lastSeen: string | null | undefined) => !!lastSeen && Date.now() - new Date(lastSeen).getTime() < ONLINE_WINDOW_MS;
const deviceLabel = (d: AnchorRemoteDevice) => d.vesselName || d.name || d.serialNumber;
const fmtAgo = (iso: string | null | undefined) => {
  if (!iso) return 'never';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
};
/** The token in groups of four, as the Scout prints it — for reading aloud or typing. */
export const groupedUrl = (token: string) => anchorRemoteUrl(token).replace(token, token.match(/.{1,4}/g)?.join(' ') ?? token);

/** Ask the relay whether a watch is live at this address. 200 = yes, 404 = no. */
async function relayIsLive(token: string): Promise<boolean> {
  try {
    const r = await fetch(anchorRelayJsonUrl(token), { cache: 'no-store', headers: { Accept: 'application/json' } });
    return r.status === 200;
  } catch {
    return false;
  }
}

export function AnchorWatchPage() {
  const addToast = useToastStore((s) => s.addToast);
  const [deviceId, setDeviceId] = useState<string | null>(null);

  const { data: access, isLoading: accessLoading } = useQuery({ queryKey: ['anchor-remote-access'], queryFn: () => api.anchorRemoteAccess() });

  const { data: devices, isLoading: devicesLoading } = useQuery({
    queryKey: ['anchor-remote-devices'],
    queryFn: () => api.anchorRemoteDevices(),
    enabled: access?.enabled === true,
    refetchInterval: DEVICES_POLL_MS,
  });

  useEffect(() => {
    if (!deviceId && devices?.length) setDeviceId(devices[0].deviceId);
  }, [devices, deviceId]);
  const device = devices?.find((d) => d.deviceId === deviceId) ?? null;
  const token = device?.token ?? null;

  // Live is the relay's word, not the Scout's.
  const { data: live } = useQuery({
    queryKey: ['anchor-remote-live', token],
    queryFn: () => relayIsLive(token as string),
    enabled: !!token,
    refetchInterval: LIVE_POLL_MS,
  });

  if (accessLoading) return <div className="p-8 text-slate-400 text-base">Loading…</div>;

  if (access && !access.enabled) {
    return (
      <div className="p-6 max-w-xl mx-auto mt-10">
        <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-8 text-center">
          <QrCodeIcon className="w-10 h-10 mx-auto text-amber-400 mb-3" />
          <h1 className="text-xl font-bold text-white mb-2">Anchor Watch remote view</h1>
          <p className="text-slate-300 mb-1">Hand your anchor watch to anyone ashore. Share the Scout’s private link from here and watch the anchor from any phone or laptop — no app, no account.</p>
          <p className="text-slate-400 text-sm mb-6">Anchor Watch remote view isn’t included in your current plan.</p>
          <Link to="/dashboard/billing" className="inline-block px-5 py-2.5 rounded-lg bg-amber-400 text-slate-900 font-bold hover:brightness-105">
            See plans that include it
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><QrCodeIcon className="w-7 h-7 text-cyan-400" /> Anchor Watch</h1>
          <p className="text-slate-300 mt-1 text-base">The same link your Scout shows on its Remote view screen, ready to share. Anyone who opens it can watch the anchor — nobody can touch it.</p>
        </div>
      </div>

      {devicesLoading && <div className="text-slate-400 text-base">Loading your Scouts…</div>}

      {!devicesLoading && devices && devices.length === 0 && (
        <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-8 text-center text-slate-300 text-base">
          No Scout is registered to your account yet.
        </div>
      )}

      {devices && devices.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {devices.map((d) => (
            <button
              key={d.deviceId}
              type="button"
              onClick={() => setDeviceId(d.deviceId)}
              className={`px-4 py-2 rounded-lg text-base font-medium border ${d.deviceId === deviceId ? 'bg-cyan-500/20 border-cyan-400 text-white' : 'bg-slate-800/60 border-slate-700 text-slate-300 hover:text-white'}`}
            >
              {deviceLabel(d)}
            </button>
          ))}
        </div>
      )}

      {device && <DeviceCard device={device} live={live === true} onCopied={() => addToast({ type: 'success', title: 'Link copied' })} />}
    </div>
  );
}

function DeviceCard({ device, live, onCopied }: { device: AnchorRemoteDevice; live: boolean; onCopied: () => void }) {
  const token = device.token;
  const online = isOnline(device.lastSeenAt);
  const fixed = device.mode === 'fixed';

  return (
    <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <span className="text-xl font-semibold text-white">{deviceLabel(device)}</span>
          <span className={`inline-flex items-center gap-1 text-sm ${online ? 'text-emerald-300' : 'text-slate-400'}`}>
            {online ? <SignalIcon className="w-4 h-4" /> : <SignalSlashIcon className="w-4 h-4" />}
            {online ? 'Scout online' : `Scout last seen ${fmtAgo(device.lastSeenAt)}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={fixed ? 'cyan' : 'slate'} label={fixed ? 'FIXED ADDRESS' : 'THIS WATCH ONLY'} />
          <Badge tone={live ? 'green' : 'slate'} label={live ? 'WATCH LIVE' : 'NO WATCH SET'} />
        </div>
      </div>

      {!token ? (
        <NoLink fixed={fixed} armed={device.armed} />
      ) : (
        <div className="grid gap-6 md:grid-cols-[252px_1fr] items-start">
          <QrPlate url={anchorRemoteUrl(token)} filename={`anchor-watch-${deviceLabel(device).replace(/\s+/g, '-').toLowerCase()}.png`} />

          <div className="min-w-0">
            <div className="text-sm font-bold tracking-widest text-slate-400 mb-1">THE ADDRESS</div>
            <div className="font-mono text-base text-cyan-200 break-all leading-relaxed mb-4">{groupedUrl(token)}</div>

            <p className="text-slate-300 text-base leading-relaxed mb-4">
              {fixed
                ? 'This address stays with the Scout. It works whenever an anchor watch is set and reads "No live watch at this link" in between. Rotate it on the Scout if it has gone to someone it should not have.'
                : 'This address belongs to the anchor watch running now. It stops working when the anchor comes up, and the next watch gets a new one.'}
            </p>

            <div className="flex flex-wrap gap-2 mb-4">
              <Action icon={ClipboardDocumentIcon} label="Copy link" onClick={async () => {
                try { await navigator.clipboard.writeText(anchorRemoteUrl(token)); onCopied(); } catch { /* the address is printed above */ }
              }} />
              {typeof navigator !== 'undefined' && 'share' in navigator && (
                <Action icon={ShareIcon} label="Share…" onClick={() => {
                  (navigator as Navigator & { share: (d: ShareData) => Promise<void> })
                    .share({ title: `Anchor watch — ${deviceLabel(device)}`, url: anchorRemoteUrl(token) })
                    .catch(() => undefined);
                }} />
              )}
              <a
                href={live ? anchorRemoteUrl(token) : undefined}
                target="_blank"
                rel="noopener noreferrer"
                aria-disabled={!live}
                title={live ? 'Open the same page a phone sees after scanning' : 'Available while an anchor watch is set on the Scout'}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-base font-semibold ${live ? 'bg-cyan-500 text-slate-900 hover:bg-cyan-400' : 'bg-slate-700/60 text-slate-400 cursor-not-allowed pointer-events-none'}`}
                onClick={(e) => {
                  if (!live) return;
                  // The page is drawn for a phone held upright, so open it in a
                  // window of that shape rather than a full-width tab (Tony,
                  // 2026-09-17). If the browser refuses a popup, the href still
                  // opens it as a tab and the page frames itself there.
                  const w = window.open(anchorRemoteUrl(token), 'vortex-anchor-remote',
                    'popup=yes,width=430,height=920,resizable=yes,scrollbars=yes');
                  if (w) { e.preventDefault(); w.focus(); }
                }}
              >
                {live ? <ArrowTopRightOnSquareIcon className="w-5 h-5" /> : <LockClosedIcon className="w-5 h-5" />}
                Open remote view
              </a>
            </div>

            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <dt className="text-slate-400">Address last changed</dt>
              <dd className="text-slate-200">{fmtAgo(device.tokenChangedAt)}</dd>
              <dt className="text-slate-400">Scout last reported</dt>
              <dd className="text-slate-200">{fmtAgo(device.updatedAt)}{device.armed ? ' · watch set' : ' · no watch set'}</dd>
            </dl>
          </div>
        </div>
      )}

      <p className="mt-6 text-sm text-slate-400 leading-relaxed">
        Read only. The alarm sounds on the boat, not here. This page and the remote view can never disarm the watch.
      </p>
    </div>
  );
}

function NoLink({ fixed, armed }: { fixed: boolean; armed: boolean }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-600 p-6 text-base text-slate-300 leading-relaxed">
      {armed
        ? 'The Scout has an anchor watch set but has not started publishing. On the Scout, tap the QR button on the Watch screen and press Start publishing.'
        : fixed
          ? 'No address yet. It is made the first time you press Start publishing on the Scout, and it is kept for the Scout after that.'
          : 'No anchor watch is set. The address is made when a watch starts and ends when the anchor comes up. Tick "Keep this address for this Scout" on the Scout to have one you can share ahead of time.'}
    </div>
  );
}

function Badge({ tone, label }: { tone: 'green' | 'cyan' | 'slate'; label: string }) {
  const cls = tone === 'green' ? 'text-emerald-300 border-emerald-400/50 bg-emerald-400/10'
    : tone === 'cyan' ? 'text-cyan-300 border-cyan-400/50 bg-cyan-400/10'
      : 'text-slate-300 border-slate-500/60 bg-slate-500/10';
  return <span className={`px-2.5 py-1 rounded-md border text-sm font-bold tracking-wider ${cls}`}>{label}</span>;
}

function Action({ icon: Icon, label, onClick }: { icon: React.ComponentType<React.SVGProps<SVGSVGElement>>; label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-base font-semibold bg-slate-700 text-white hover:bg-slate-600">
      <Icon className="w-5 h-5" /> {label}
    </button>
  );
}

/** The QR on a white plate with a real quiet zone, and a Save image button. */
function QrPlate({ url, filename }: { url: string; filename: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    QRCode.toCanvas(c, url, { width: 220, margin: 0, errorCorrectionLevel: 'M', color: { dark: '#000000', light: '#ffffff' } })
      .then(() => setFailed(false)).catch(() => setFailed(true));
  }, [url]);
  const save = () => {
    const c = canvasRef.current;
    if (!c) return;
    // A saved image needs its own quiet zone: draw the code onto a larger white canvas.
    const out = document.createElement('canvas');
    out.width = c.width + 64; out.height = c.height + 64;
    const g = out.getContext('2d');
    if (!g) return;
    g.fillStyle = '#ffffff'; g.fillRect(0, 0, out.width, out.height);
    g.drawImage(c, 32, 32);
    const a = document.createElement('a');
    a.href = out.toDataURL('image/png'); a.download = filename; a.click();
  };
  return (
    <div>
      <div className="w-[252px] h-[252px] p-4 bg-white rounded-xl flex items-center justify-center">
        {failed
          ? <div className="text-black text-sm text-center">Could not draw the code — use the address</div>
          : <canvas ref={canvasRef} className="block w-[220px] h-[220px]" role="img" aria-label="QR code for the anchor watch remote view" />}
      </div>
      <button type="button" onClick={save} className="mt-3 inline-flex items-center gap-2 text-base font-medium text-cyan-300 hover:text-cyan-200">
        <ArrowDownTrayIcon className="w-5 h-5" /> Save QR image
      </button>
      <div className="mt-1 inline-flex items-center gap-1 text-sm text-slate-400"><ArrowPathIcon className="w-4 h-4" /> Same code as the Scout screen</div>
    </div>
  );
}
